// SimulationPage.js

import React, { useEffect, useState, useMemo } from "react";

import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
} from "@mui/material";

import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";

import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TableChartIcon from "@mui/icons-material/TableChart";

import api from "../api";

function SimulationPage() {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [view, setView] = useState(null); // null | backfill | simulation | table | compare
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const [leftLoading, setLeftLoading] = useState(false);
  const [rightLoading, setRightLoading] = useState(false);

  const [tables, setTables] = useState([]);
  const [activeTable, setActiveTable] = useState(null);
  const [tableData, setTableData] = useState([]);

  // LEFT COMPARE DATA
  const [leftTable, setLeftTable] = useState(null);
  const [leftTrades, setLeftTrades] = useState([]);
  const [leftSummary, setLeftSummary] = useState(null);
  const [leftPerformance, setLeftPerformance] = useState(null);
  const [leftTradeInsights, setLeftTradeInsights] = useState(null);
  const [leftYearlyStats, setLeftYearlyStats] = useState([]);

  // RIGHT COMPARE DATA
  const [rightTable, setRightTable] = useState(null);
  const [rightTrades, setRightTrades] = useState([]);
  const [rightSummary, setRightSummary] = useState(null);
  const [rightPerformance, setRightPerformance] = useState(null);
  const [rightTradeInsights, setRightTradeInsights] = useState(null);
  const [rightYearlyStats, setRightYearlyStats] = useState([]);

  const [tf, setTf] = useState("5");
  const [fast, setFast] = useState("36");
  const [slow, setSlow] = useState("103");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tableToDelete, setTableToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const limit = 500;
  const [offset, setOffset] = useState(0);
  const [totalRows, setTotalRows] = useState(0);

  const [summary, setSummary] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [tradeInsights, setTradeInsights] = useState(null);
  const [yearlyStats, setYearlyStats] = useState([]);

  const deleteTable = async () => {
    if (!tableToDelete) return;

    try {
      setDeleting(true);

      await api.delete("/simulation_table_data", {
        params: { table: tableToDelete },
      });

      setTables((prev) => prev.filter((t) => t !== tableToDelete));

      if (activeTable === tableToDelete) {
        setActiveTable(null);
        setTableData([]);
      }

      setDeleteDialogOpen(false);
      setTableToDelete(null);
    } catch (err) {
      console.error("Delete failed", err);
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(() => {
    if (!tableData.length) return [];
    return Object.keys(tableData[0]);
  }, [tableData]);

  const rows = tableData;

  // -----------------------------
  // LOAD SIMULATION TABLES
  // -----------------------------
  const loadSimulationTables = async () => {
    try {
      const res = await api.get("/simulation_tables");

      setTables(res.data.tables || []);
    } catch (err) {
      console.error("Failed to load simulation tables", err);
    }
  };

  useEffect(() => {
    loadSimulationTables();
  }, []);

  // -----------------------------
  // BACKFILL
  // -----------------------------
  const runBackfill = async () => {
    try {
      setLoading(true);
      setResult(null);

      const res = await api.get("/BackfillCandles");

      setResult(res.data);
    } catch (err) {
      setResult({
        success: false,
        error: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // RUN SIMULATION
  // -----------------------------
  const runSimulation = async () => {
    try {
      setLoading(true);
      setResult(null);

      const res = await api.post(
        `/simulate_sma_x?TF=${tf}&fast=${fast}&slow=${slow}`,
      );

      setResult(res.data);
    } catch (err) {
      setResult({
        success: false,
        error: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // LOAD TABLE DATA
  // -----------------------------
  const loadTableData = async (table, newOffset = 0) => {
    try {
      setLoading(true);

      const res = await api.get("/simulation_table_data", {
        params: {
          table,
          limit,
          offset: newOffset,
        },
      });

      const data = res.data;

      setTableData(data.trades || []);
      setTotalRows(data.pagination?.total || 0);
      setOffset(newOffset);

      setSummary(data.summary);
      setPerformance(data.performanceMetrics);
      setTradeInsights(data.tradeInsights);
      setYearlyStats(data.yearlyPerformance || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCompareTable = async (table, side) => {
    try {
      if (side === "left") setLeftLoading(true);
      if (side === "right") setRightLoading(true);

      const res = await api.get("/simulation_table_data", {
        params: {
          table,
          limit,
          offset: 0,
        },
      });

      const data = res.data;

      if (side === "left") {
        setLeftTable(table);
        setLeftTrades(data.trades || []);
        setLeftSummary(data.summary);
        setLeftPerformance(data.performanceMetrics);
        setLeftTradeInsights(data.tradeInsights);
        setLeftYearlyStats(data.yearlyPerformance || []);
      }

      if (side === "right") {
        setRightTable(table);
        setRightTrades(data.trades || []);
        setRightSummary(data.summary);
        setRightPerformance(data.performanceMetrics);
        setRightTradeInsights(data.tradeInsights);
        setRightYearlyStats(data.yearlyPerformance || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (side === "left") setLeftLoading(false);
      if (side === "right") setRightLoading(false);
    }
  };

  const SidebarButton = React.memo(({ icon, label, active, onClick }) => (
    <Button
      onClick={onClick}
      startIcon={icon}
      sx={{
        mb: 0.5,
        width: "100%",
        justifyContent: sidebarOpen ? "flex-start" : "center",
        textTransform: "none",
        fontSize: 12,
        fontWeight: active ? 700 : 500,
        color: active ? "#1976d2" : "#333",
        backgroundColor: active ? "rgba(25,118,210,0.12)" : "transparent",
      }}
    >
      {sidebarOpen && label}
    </Button>
  ));

  const formatMoney = (value) => {
    if (value === null || value === undefined) return "-";

    const num = Number(value);

    if (Number.isNaN(num)) return "-";

    return `₹${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return "-";

    const num = Number(value);

    if (Number.isNaN(num)) return value;

    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const Metric = ({ label, value }) => {
    let formatted = value;

    if (typeof value === "number") {
      formatted = formatNumber(value);
    }

    return (
      <Box sx={{ minWidth: 120, flex: "1 1 auto" }}>
        <Typography fontSize={12} color="text.secondary">
          {label}
        </Typography>

        <Typography fontWeight={700}>{formatted}</Typography>
      </Box>
    );
  };

  const StrategyPanel = ({
    table,
    trades,
    summary,
    performance,
    tradeInsights,
    yearlyStats,
    loading,
  }) => {
    // const columns = useMemo(() => {
    //   if (!trades.length) return [];
    //   return Object.keys(trades[0]);
    // }, [trades]);

    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!table) {
      return (
        <Typography color="text.secondary">
          Select a table to view strategy
        </Typography>
      );
    }

    return (
      <Box>
        {summary && (
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption">Total Spot PnL</Typography>
              <Typography fontWeight={700}>
                {formatMoney(summary.totalSpotPnL)}
              </Typography>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="caption">Total Trades</Typography>
              <Typography fontWeight={700}>{summary.totalTrades}</Typography>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="caption">Option LTP</Typography>
              <Typography fontWeight={700}>
                {formatMoney(summary.optionLTP)}
              </Typography>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="caption">Spot:LTP Ratio</Typography>
              <Typography fontWeight={700}>
                {formatNumber(summary.spotToLtpRatio)}
              </Typography>
            </Paper>
          </Box>
        )}

        {performance && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography fontWeight={700} sx={{ mb: 2 }}>
              Performance Metrics
            </Typography>

            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <Metric label="Win Rate" value={performance.winRate} />
              <Metric label="Avg Win" value={`₹${performance.avgWin}`} />
              <Metric label="Avg Loss" value={`₹${performance.avgLoss}`} />
              <Metric label="Profit Factor" value={performance.profitFactor} />
              <Metric label="Sharpe" value={performance.sharpe} />
              <Metric label="Max DD" value={`₹${performance.maxDrawdown}`} />
            </Box>
          </Paper>
        )}

        {tradeInsights && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography fontWeight={700} sx={{ mb: 2 }}>
              Trade Insights
            </Typography>

            <Box sx={{ display: "flex", gap: 3 }}>
              <Metric
                label="Best Trade"
                value={formatMoney(tradeInsights.bestTrade)}
              />
              <Metric
                label="Worst Trade"
                value={formatMoney(tradeInsights.worstTrade)}
              />
              <Metric label="Win Streak" value={tradeInsights.winStreak} />
              <Metric label="Loss Streak" value={tradeInsights.lossStreak} />
            </Box>
          </Paper>
        )}

        {yearlyStats.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography fontWeight={700} sx={{ mb: 2 }}>
              Yearly Performance
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Year</TableCell>
                  <TableCell>Trades</TableCell>
                  <TableCell>Win %</TableCell>
                  <TableCell>Profit</TableCell>
                  <TableCell>Loss</TableCell>
                  <TableCell>Net PnL</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {yearlyStats.map((y) => (
                  <TableRow key={y.year}>
                    <TableCell>{y.year}</TableCell>
                    <TableCell>{y.total_trades}</TableCell>
                    <TableCell>{formatNumber(y.win_rate_percent)}%</TableCell>

                    <TableCell style={{ color: "green" }}>
                      {formatMoney(y.total_profit)}
                    </TableCell>

                    <TableCell style={{ color: "red" }}>
                      {formatMoney(y.total_loss)}
                    </TableCell>

                    <TableCell
                      style={{
                        color: y.net_pnl >= 0 ? "green" : "red",
                        fontWeight: 700,
                      }}
                    >
                      {formatMoney(y.net_pnl)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        {/* {trades.length > 0 && (
          <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell key={c}>{c}</TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {trades.map((row, i) => (
                  <TableRow key={i}>
                    {columns.map((c) => (
                      <TableCell key={c}>{row[c]}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )} */}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
      }}
    >
      {/* LEFT SIDEBAR */}
      <Box
        sx={{
          width: sidebarOpen ? 260 : 64,
          minWidth: sidebarOpen ? 260 : 64,
          transition: "width 0.25s ease",
          borderRight: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: sidebarOpen ? "space-between" : "center",
            px: 2,
            py: 1,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          {sidebarOpen && (
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: "#4a93ff",
              }}
            >
              SIMULATION
            </Typography>
          )}

          <Button size="small" onClick={() => setSidebarOpen((v) => !v)}>
            {sidebarOpen ? "⟨" : "☰"}
          </Button>
        </Box>

        {/* MENU */}
        <Box sx={{ p: 1, overflowY: "auto" }}>
          {/* SIMULATION SECTION */}
          {sidebarOpen && (
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: "#4a93ff",
                mb: 1,
              }}
            >
              SIMULATION
            </Typography>
          )}

          <SidebarButton
            icon={<CloudDownloadIcon fontSize="small" />}
            label="Backfill"
            active={view === "backfill"}
            onClick={() => {
              setView("backfill");
              setResult(null);

              setSummary(null);
              setPerformance(null);
              setTradeInsights(null);
              setYearlyStats([]);
            }}
          />

          <SidebarButton
            icon={<PlayArrowIcon fontSize="small" />}
            label="Simulation"
            active={view === "simulation"}
            onClick={() => {
              setView("simulation");
              setResult(null);

              setSummary(null);
              setPerformance(null);
              setTradeInsights(null);
              setYearlyStats([]);
            }}
          />

          <SidebarButton
            icon={<TableChartIcon fontSize="small" />}
            label="Compare"
            active={view === "compare"}
            onClick={() => {
              setView("compare");

              setSummary(null);
              setPerformance(null);
              setTradeInsights(null);
              setYearlyStats([]);
            }}
          />

          {/* DATA SECTION */}
          {sidebarOpen && (
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: "#4a93ff",
                mt: 2,
                mb: 1,
              }}
            >
              DATA
            </Typography>
          )}

          {tables.map((table) => (
            <Box
              key={table}
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Button
                startIcon={<TableChartIcon fontSize="small" />}
                onClick={() => {
                  setView("table");
                  setActiveTable(table);
                  setOffset(0);
                  loadTableData(table, 0);
                }}
                sx={{
                  flex: 1,
                  justifyContent: sidebarOpen ? "flex-start" : "center",
                  textTransform: "none",
                  fontSize: 12,
                  color: activeTable === table ? "#1976d2" : "#333",
                  backgroundColor:
                    activeTable === table
                      ? "rgba(25,118,210,0.12)"
                      : "transparent",
                }}
              >
                {sidebarOpen && table}
              </Button>

              {sidebarOpen && (
                <Tooltip title="Delete table">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setTableToDelete(table);
                      setDeleteDialogOpen(true);
                    }}
                    sx={{
                      color: "#d32f2f",
                      transition: "0.2s",
                      "&:hover": {
                        backgroundColor: "rgba(211,47,47,0.1)",
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* RIGHT PANEL */}
      <Box
        sx={{
          flex: 1,
          p: 3,
          overflowY: "auto",
        }}
      >
        {/* EMPTY DEFAULT */}
        {view === null && null}

        {/* BACKFILL VIEW */}
        {view === "backfill" && (
          <>
            <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 3 }}>
              Backfill Candles
            </Typography>

            <Button
              variant="contained"
              startIcon={<CloudDownloadIcon />}
              onClick={runBackfill}
              disabled={loading}
            >
              Run Backfill
            </Button>

            {loading && (
              <Box sx={{ mt: 3 }}>
                <CircularProgress />
              </Box>
            )}

            {result && (
              <Paper sx={{ mt: 3, p: 2 }}>
                <pre>{JSON.stringify(result, null, 2)}</pre>
              </Paper>
            )}
          </>
        )}

        {/* SIMULATION VIEW */}
        {view === "simulation" && (
          <>
            <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 3 }}>
              SMA Crossover Simulation
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                label="Timeframes"
                value={tf}
                onChange={(e) => setTf(e.target.value)}
                size="small"
              />

              <TextField
                label="Fast SMA"
                value={fast}
                onChange={(e) => setFast(e.target.value)}
                size="small"
              />

              <TextField
                label="Slow SMA"
                value={slow}
                onChange={(e) => setSlow(e.target.value)}
                size="small"
              />
            </Box>

            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={runSimulation}
              disabled={loading}
            >
              Run Simulation
            </Button>

            {loading && (
              <Box sx={{ mt: 3 }}>
                <CircularProgress />
              </Box>
            )}

            {result && (
              <Paper sx={{ mt: 3, p: 2 }}>
                <pre>{JSON.stringify(result, null, 2)}</pre>
              </Paper>
            )}
          </>
        )}

        {/* TABLE VIEW */}

        {view === "table" && summary && (
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption">Total Spot PnL</Typography>
              <Typography fontWeight={700}>
                {formatMoney(summary.totalSpotPnL)}
              </Typography>{" "}
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="caption">Total Trades</Typography>
              <Typography fontWeight={700}>{summary.totalTrades}</Typography>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="caption">Option LTP</Typography>
              <Typography fontWeight={700}>
                {formatMoney(summary.optionLTP)}
              </Typography>{" "}
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="caption">Spot:LTP Ratio</Typography>
              <Typography fontWeight={700}>
                {formatNumber(summary.spotToLtpRatio)}
              </Typography>{" "}
            </Paper>
          </Box>
        )}

        {view === "table" && performance && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography fontWeight={700} sx={{ mb: 2 }}>
              Performance Metrics
            </Typography>

            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <Metric label="Win Rate" value={performance.winRate} />
              <Metric label="Avg Win" value={`₹${performance.avgWin}`} />
              <Metric label="Avg Loss" value={`₹${performance.avgLoss}`} />
              <Metric label="Profit Factor" value={performance.profitFactor} />
              <Metric label="Sharpe" value={performance.sharpe} />
              <Metric label="Max DD" value={`₹${performance.maxDrawdown}`} />
            </Box>
          </Paper>
        )}

        {view === "table" && tradeInsights && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography fontWeight={700} sx={{ mb: 2 }}>
              Trade Insights
            </Typography>

            <Box sx={{ display: "flex", gap: 3 }}>
              <Metric
                label="Best Trade"
                value={formatMoney(tradeInsights.bestTrade)}
              />
              <Metric
                label="Worst Trade"
                value={formatMoney(tradeInsights.worstTrade)}
              />
              <Metric label="Win Streak" value={tradeInsights.winStreak} />
              <Metric label="Loss Streak" value={tradeInsights.lossStreak} />
            </Box>
          </Paper>
        )}

        {view === "table" && yearlyStats.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography fontWeight={700} sx={{ mb: 2 }}>
              Yearly Performance
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Year</TableCell>
                  <TableCell>Trades</TableCell>
                  <TableCell>Win %</TableCell>
                  <TableCell>Profit</TableCell>
                  <TableCell>Loss</TableCell>
                  <TableCell>Net PnL</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {yearlyStats.map((y) => (
                  <TableRow key={y.year}>
                    <TableCell>{y.year}</TableCell>
                    <TableCell>{y.total_trades}</TableCell>
                    <TableCell>
                      {formatNumber(y.win_rate_percent)}%
                    </TableCell>{" "}
                    <TableCell style={{ color: "green" }}>
                      {formatMoney(y.total_profit)}
                    </TableCell>{" "}
                    <TableCell style={{ color: "red" }}>
                      {formatMoney(y.total_loss)}
                    </TableCell>{" "}
                    <TableCell
                      style={{
                        color: y.net_pnl >= 0 ? "green" : "red",
                        fontWeight: 700,
                      }}
                    >
                      {formatMoney(y.net_pnl)}
                    </TableCell>{" "}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        {view === "table" && (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
                {activeTable}
              </Typography>
              <Button variant="outlined" onClick={() => setView("compare")}>
                Compare Tables
              </Button>
            </Box>
            {loading ? (
              <CircularProgress />
            ) : tableData.length === 0 ? (
              <Typography>No records found.</Typography>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: "70vh",
                  overflow: "auto",
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{ fontFamily: "monospace" }}
                >
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      {columns.map((key) => (
                        <TableCell key={key} sx={{ fontWeight: 700 }}>
                          {key}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          backgroundColor:
                            row.direction === "BULL"
                              ? "rgba(0,200,0,0.08)"
                              : row.direction === "BEAR"
                                ? "rgba(255,0,0,0.08)"
                                : "inherit",
                        }}
                      >
                        {columns.map((key) => (
                          <TableCell
                            key={key}
                            sx={{
                              color:
                                typeof row[key] === "number" && row[key] < 0
                                  ? "red"
                                  : "inherit",
                            }}
                          >
                            {typeof row[key] === "number"
                              ? formatNumber(row[key])
                              : (row[key] ?? "-")}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>{" "}
                </Table>
              </TableContainer>
            )}

            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <Button
                disabled={offset === 0}
                onClick={() => loadTableData(activeTable, offset - limit)}
              >
                Previous
              </Button>

              <Button
                disabled={offset + limit >= totalRows}
                onClick={() => loadTableData(activeTable, offset + limit)}
              >
                Next
              </Button>

              <Typography sx={{ ml: 2 }}>
                Showing {offset + 1} - {Math.min(offset + limit, totalRows)} of{" "}
                {totalRows}
              </Typography>
            </Box>
          </>
        )}

        {view === "compare" && (
          <Box
            sx={{
              display: "flex",
              gap: 3,
              width: "100%",
              alignItems: "flex-start",
            }}
          >
            {" "}
            {/* LEFT */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {" "}
              <Typography fontWeight={700} sx={{ mb: 2 }}>
                Strategy A
              </Typography>
              <TextField
                select
                fullWidth
                SelectProps={{ native: true }}
                value={leftTable || ""}
                onChange={(e) => loadCompareTable(e.target.value, "left")}
                sx={{ mb: 3 }}
              >
                <option value="">Select table</option>
                {tables.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </TextField>
              <StrategyPanel
                table={leftTable}
                trades={leftTrades}
                summary={leftSummary}
                performance={leftPerformance}
                tradeInsights={leftTradeInsights}
                yearlyStats={leftYearlyStats}
                loading={leftLoading}
              />{" "}
            </Box>
            {/* RIGHT */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {" "}
              <Typography fontWeight={700} sx={{ mb: 2 }}>
                Strategy B
              </Typography>
              <TextField
                select
                fullWidth
                SelectProps={{ native: true }}
                value={rightTable || ""}
                onChange={(e) => loadCompareTable(e.target.value, "right")}
                sx={{ mb: 3 }}
              >
                <option value="">Select table</option>
                {tables.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </TextField>
              <StrategyPanel
                table={rightTable}
                trades={rightTrades}
                summary={rightSummary}
                performance={rightPerformance}
                tradeInsights={rightTradeInsights}
                yearlyStats={rightYearlyStats}
                loading={rightLoading}
              />
            </Box>
          </Box>
        )}
      </Box>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Table</DialogTitle>

        <DialogContent>
          <Typography>Are you sure you want to delete table:</Typography>

          <Typography
            sx={{
              fontWeight: 700,
              color: "#d32f2f",
              mt: 1,
            }}
          >
            {tableToDelete}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={deleteTable}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SimulationPage;
